import { useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/analytics`;

// Generate or retrieve session ID
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('bb_session_id');
  if (!sessionId) {
    sessionId = `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('bb_session_id', sessionId);
  }
  return sessionId;
};

// Get device type from user agent
const getDeviceType = () => {
  const ua = navigator.userAgent;
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return 'tablet';
  }
  if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
    return 'mobile';
  }
  return 'desktop';
};

// Get browser name
const getBrowser = () => {
  const ua = navigator.userAgent;
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('SamsungBrowser')) return 'Samsung';
  if (ua.includes('Opera') || ua.includes('OPR')) return 'Opera';
  if (ua.includes('Trident')) return 'IE';
  if (ua.includes('Edge')) return 'Edge';
  if (ua.includes('Edg')) return 'Edge';
  if (ua.includes('Chrome')) return 'Chrome';
  if (ua.includes('Safari')) return 'Safari';
  return 'Other';
};

// Get OS
const getOS = () => {
  const ua = navigator.userAgent;
  if (ua.includes('Win')) return 'Windows';
  if (ua.includes('Mac')) return 'MacOS';
  if (ua.includes('Linux')) return 'Linux';
  if (ua.includes('Android')) return 'Android';
  if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
  return 'Other';
};

// Get UTM parameters from URL
const getUTMParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source'),
    utm_medium: params.get('utm_medium'),
    utm_campaign: params.get('utm_campaign'),
    utm_term: params.get('utm_term'),
    utm_content: params.get('utm_content'),
  };
};

export const useAnalytics = () => {
  const startTimeRef = useRef(Date.now());
  const sessionId = useRef(getSessionId());
  const currentPage = useRef(window.location.pathname);
  const hasTrackedRef = useRef(false);

  // Track page visit
  const trackVisit = useCallback(async () => {
    // Don't track dashboard visits
    if (window.location.pathname === '/dashboard') return;
    
    try {
      const utm = getUTMParams();
      await axios.post(`${API}/track-visit`, {
        session_id: sessionId.current,
        page_url: window.location.pathname,
        referrer: document.referrer || null,
        utm_source: utm.utm_source,
        utm_medium: utm.utm_medium,
        utm_campaign: utm.utm_campaign,
        utm_term: utm.utm_term,
        utm_content: utm.utm_content,
        device_type: getDeviceType(),
        browser: getBrowser(),
        os: getOS(),
        screen_width: window.screen.width,
        screen_height: window.screen.height,
        language: navigator.language,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
    } catch (e) {
      console.error('Analytics tracking error:', e);
    }
  }, []);

  // Update time spent
  const updateTimeSpent = useCallback(async () => {
    if (window.location.pathname === '/dashboard') return;
    
    const timeSpent = Math.round((Date.now() - startTimeRef.current) / 1000);
    try {
      await axios.post(`${API}/update-time`, {
        session_id: sessionId.current,
        page_url: currentPage.current,
        time_spent_seconds: timeSpent,
      });
    } catch (e) {
      // Silent fail for time updates
    }
  }, []);

  // Track click
  const trackClick = useCallback(async (event) => {
    if (window.location.pathname === '/dashboard') return;
    
    const target = event.target;
    try {
      await axios.post(`${API}/track-click`, {
        session_id: sessionId.current,
        page_url: window.location.pathname,
        element_id: target.id || null,
        element_class: target.className?.toString() || null,
        element_text: target.innerText?.substring(0, 100) || null,
        element_tag: target.tagName,
        x_position: event.clientX,
        y_position: event.clientY,
      });
    } catch (e) {
      // Silent fail for click tracking
    }
  }, []);

  useEffect(() => {
    // Track initial visit only once
    // Don't track the dashboard
    if (!hasTrackedRef.current && window.location.pathname !== '/dashboard') {
      trackVisit();
      hasTrackedRef.current = true;
    }

    // Track clicks on important elements (buttons, links)
    const handleClick = (e) => {
      const target = e.target.closest('a, button, [data-track]');
      if (target) {
        trackClick(e);
      }
    };

    // Update time spent periodically and on page leave
    const intervalId = setInterval(updateTimeSpent, 30000); // Every 30 seconds

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        updateTimeSpent();
      }
    };

    const handleBeforeUnload = () => {
      updateTimeSpent();
    };

    document.addEventListener('click', handleClick);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('click', handleClick);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      updateTimeSpent();
    };
  }, [trackVisit, trackClick, updateTimeSpent]);

  return { sessionId: sessionId.current };
};
